import type { MetaFunction } from '@remix-run/node';
import WordGlory from '~/assets/word-glory.svg';
import { generateMeta } from '~/services/meta';
import { ArrowRight } from 'lucide-react';
import { Link } from '@remix-run/react';
import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import WordLogo from '~/assets/word.svg';
import OutlookLogo from '~/assets/outlook.svg';
import TeamsLogo from '~/assets/teams.svg';
import ChromeLogo from '~/assets/chrome.svg';
import TelegramLogo from '~/assets/telegram.svg';
import ExcelLogo from '~/assets/excel.svg';
import PowerPointLogo from '~/assets/powerpoint.svg';
import GmailLogo from '~/assets/gmail.svg';
import OneNoteLogo from '~/assets/onenote.svg';
import { EmblaCarouselForReviews } from '~/components/EmblaCarousel';
import { Button } from '~/components/ui/button';
import { MicroLandingNew } from '~/components/ProductMicroLanding';

export const meta: MetaFunction = () => {
    return generateMeta({
        title: 'ChatGPT Add-In for Microsoft Word',
        description: `Improve your writing, fix your tone, and say exactly what you mean — without leaving Microsoft Word.`,
    });
};

function IntegrationCard({ item }) {
    return (
        <Link
            className='bg-white group block overflow-hidden w-full border border-1 rounded-xl hover:border-blue-600'
            to={item.url}>
            <div className='flex flex-col p-4 gap-4'>
                <div className='w-full gap-2 flex flex-col items-center justify-start'>
                    <p className='text-gray-600 text-ellipsis max-w-64 whitespace-nowrap overflow-hidden mt-2 sm:mt-3 text-lg md:text-xl font-pj'>
                        {item.name}
                    </p>
                    <img src={item.imgSrc} className='w-32 h-32' />
                </div>
                <Button variant='outline'>
                    Install Add-In
                    <ArrowRight />
                </Button>
            </div>
        </Link>
    );
}

const instruction = {
    label: 'ChatGPT Add-In for Microsoft Word',
    videoId: '0a8a672363dd4d47864553ce010f9773',
    paddingTop: '72.5%',
    steps: [
        <>Highlight any part of the text in Microsoft Word document</>,
        <>
            Open AI Perfect Assistant, select any AI Prompt Template and Click
            "Generate with AI"
        </>,
        <>Click "Insert" to insert AI generated text to the Word document</>,
    ],
};

export const Demonstration = ({ videoDefinition }) => {
    return (
        <div
            style={{
                position: 'relative',
                paddingTop: videoDefinition.paddingTop,
            }}>
            <iframe
                title={`${videoDefinition.label} - AI Perfect Assistant`}
                src={`https://customer-b5iz2r6cpeptxyuk.cloudflarestream.com/${videoDefinition.videoId}/iframe?muted=true&preload=true&loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-b5iz2r6cpeptxyuk.cloudflarestream.com%2F${videoDefinition.videoId}%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600&controls=true`}
                loading='lazy'
                style={{
                    border: 'none',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: '100%',
                }}
                className='rounded-2xl'
                allow='accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;'
                allowFullScreen
            />
        </div>
    );
};

function GreeenCheckBox() {
    return (
        <svg
            className='w-5 h-5 mx-auto'
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 20 20'
            fill='currentColor'>
            <path
                fill-rule='evenodd'
                d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                clip-rule='evenodd'></path>
        </svg>
    );
}

export function AlternativeToSection() {
    return (
        <section className='py-10 bg-white sm:py-16 lg:py-24'>
            <div className='px-4 mx-auto max-w-7xl sm:px-6 lg:px-8'>
                <div className='max-w-xl mx-auto text-center'>
                    <h2 className='text-4xl font-bold text-black lg:text-5xl sm:text-5xl'>
                        How Does It Compare to Grammarly &amp; QuillBot?
                    </h2>
                    <p className='mt-4 text-lg leading-relaxed text-gray-600'>
                        More Than Grammar. 60+ Tools. Full Office Integration
                    </p>
                </div>

                {/*<!-- lg+ -->*/}
                <div className='hidden mt-16 lg:block'>
                    <table className='w-full'>
                        <thead>
                            <tr>
                                <th className='py-8 pr-4'></th>

                                <th className='px-4 py-8 text-center'>
                                    <span className='text-base font-medium text-blue-600'>
                                        {' '}
                                        Grammarly{' '}
                                    </span>
                                </th>

                                <th className='px-4 py-8 text-center'>
                                    <span className='text-base font-medium text-blue-600'>
                                        {' '}
                                        QuillBot{' '}
                                    </span>
                                </th>

                                <th className='px-4 py-8 text-center bg-gray-900 rounded-t-xl'>
                                    <span className='px-4 py-2 text-base font-medium text-white bg-blue-600 rounded-full'>
                                        {' '}
                                        AI Perfect Assistant{' '}
                                    </span>
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr>
                                <td className='py-4 pr-4 font-medium border-b border-gray-200'>
                                    Works natively inside <br />
                                    Word, Outlook, Excel, PowerPoint
                                </td>

                                <td className='px-4 py-4 text-center border-b border-gray-200'>
                                    -
                                </td>

                                <td className='px-4 py-4 text-center border-b border-gray-200'>
                                    Only Word
                                </td>

                                <td className='px-4 py-4 text-center text-white bg-gray-900 border-b border-white/20'>
                                    <GreeenCheckBox />
                                </td>
                            </tr>

                            <tr>
                                <td className='py-4 pr-4 font-medium border-b border-gray-200'>
                                    Supports 60+ AI tools
                                    <br />
                                    (not just grammar/paraphrasing)
                                </td>

                                <td className='px-4 py-4 text-center border-b border-gray-200'>
                                    -
                                </td>

                                <td className='px-4 py-4 text-center border-b border-gray-200'>
                                    -
                                </td>

                                <td className='px-4 py-4 text-center text-white bg-gray-900 border-b border-white/20'>
                                    <GreeenCheckBox />
                                </td>
                            </tr>

                            <tr>
                                <td className='py-4 pr-4 font-medium border-b border-gray-200'>
                                    Cheaper than
                                    <br />
                                    Grammarly Business or QuillBot Premium
                                </td>

                                <td className='px-4 py-4 text-center border-b border-gray-200'>
                                    -
                                </td>

                                <td className='px-4 py-4 text-center border-b border-gray-200'>
                                    -
                                </td>

                                <td className='px-4 py-4 text-center text-white bg-gray-900 border-b border-white/20'>
                                    <GreeenCheckBox />
                                </td>
                            </tr>

                            <tr>
                                <td className='py-4 pr-4 font-medium border-b border-gray-200'>
                                    Generate reports, summaries, meeting notes
                                    in one click
                                </td>

                                <td className='px-4 py-4 text-center border-b border-gray-200'>
                                    -
                                </td>

                                <td className='px-4 py-4 text-center border-b border-gray-200'>
                                    -
                                </td>

                                <td className='px-4 py-4 text-center text-white bg-gray-900 border-b border-white/20'>
                                    <GreeenCheckBox />
                                </td>
                            </tr>

                            <tr>
                                <td className='py-4 pr-4 font-medium border-b border-gray-200'>
                                    Draft professional replies based on selected
                                    input
                                </td>

                                <td className='px-4 py-4 text-center border-b border-gray-200'>
                                    -
                                </td>

                                <td className='px-4 py-4 text-center border-b border-gray-200'>
                                    -
                                </td>

                                <td className='px-4 py-4 text-center text-white bg-gray-900 border-b border-white/20'>
                                    <GreeenCheckBox />
                                </td>
                            </tr>

                            <tr>
                                <td className='py-6 pr-4'></td>

                                <td className='px-4 py-6 text-center'></td>

                                <td className='px-4 py-6 text-center'></td>

                                <td className='px-4 py-6 text-center text-white bg-yellow-500 rounded-b-xl'>
                                    <Link
                                        to='/'
                                        title='Explore Integrations'
                                        className='inline-flex items-center font-semibold text-white'>
                                        Explore Integrations{' '}
                                        <ArrowRight className='ml-1' />
                                    </Link>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {/*<!-- xs to lg -->*/}
            <div className='block mt-12 border-t border-b border-gray-200 divide-y divide-gray-200 lg:hidden'>
                <div className='grid grid-cols-3  text-center divide-x divide-gray-200'>
                    <div className='px-2 py-2'>
                        <span className='text-sm font-medium text-blue-600'>
                            Grammarly
                        </span>
                    </div>

                    <div className='px-2 py-2'>
                        <span className='text-sm font-medium text-blue-600'>
                            {' '}
                            QuillBot{' '}
                        </span>
                    </div>

                    <div className='px-2 py-2'>
                        <span className='text-sm font-medium text-blue-600'>
                            {' '}
                            AI Perfect Assistant{' '}
                        </span>
                    </div>
                </div>

                <div className='px-2 py-4 sm:px-4'>
                    <p className='font-semibold'>
                        Works natively inside <br />
                        Word, Outlook, Excel, PowerPoint
                    </p>
                </div>

                <div className='grid grid-cols-3  text-center divide-x divide-gray-200'>
                    <div className='px-2 py-2'>-</div>

                    <div className='px-2 py-2'>Only Word</div>

                    <div className='px-2 py-2'>
                        <GreeenCheckBox />
                    </div>
                </div>

                <div className='px-2 py-4 sm:px-4'>
                    <p className='font-semibold'>
                        Supports 60+ AI tools
                        <br />
                        (not just grammar/paraphrasing)
                    </p>
                </div>

                <div className='grid grid-cols-3  text-center divide-x divide-gray-200'>
                    <div className='px-2 py-2'>-</div>

                    <div className='px-2 py-2'>-</div>

                    <div className='px-2 py-2'>
                        <GreeenCheckBox />
                    </div>
                </div>

                <div className='px-2 py-4 sm:px-4'>
                    <p className='font-semibold'>
                        Cheaper than
                        <br />
                        Grammarly Business or QuillBot Premium
                    </p>
                </div>

                <div className='grid grid-cols-3  text-center divide-x divide-gray-200'>
                    <div className='px-2 py-2'>-</div>

                    <div className='px-2 py-2'>-</div>

                    <div className='px-2 py-2'>
                        <GreeenCheckBox />
                    </div>
                </div>

                <div className='px-2 py-4 sm:px-4'>
                    <p className='font-semibold'>
                        Generate reports, summaries, meeting notes in one click
                    </p>
                </div>

                <div className='grid grid-cols-3  text-center divide-x divide-gray-200'>
                    <div className='px-2 py-2'>-</div>

                    <div className='px-2 py-2'>-</div>

                    <div className='px-2 py-2'>
                        <GreeenCheckBox />
                    </div>
                </div>

                <div className='px-2 py-4 sm:px-4'>
                    <p className='font-semibold'>
                        Draft professional replies based on selected input
                    </p>
                </div>

                <div className='grid grid-cols-3 text-center divide-x divide-gray-200'>
                    <div className='px-2 py-2'>-</div>

                    <div className='px-2 py-2'>-</div>

                    <div className='px-2 py-2'>
                        <GreeenCheckBox />
                    </div>
                </div>

                <div className='grid grid-cols-3 text-center divide-x divide-gray-200'>
                    <div className='px-1 py-2 sm:px-4'>
                        <span className='text-sm font-medium text-blue-600'>
                            {' '}
                            Grammarly{' '}
                        </span>
                    </div>

                    <div className='px-1 py-2 sm:px-4'>
                        <span className='text-sm font-medium text-blue-600'>
                            {' '}
                            QuillBot{' '}
                        </span>
                    </div>

                    <div className='px-1 py-2 sm:px-4'>
                        <span className='text-sm font-medium text-blue-600'>
                            {' '}
                            AI Perfect Assistant{' '}
                        </span>

                        <Link
                            to='/'
                            title='Explore Integrations'
                            className='flex items-center justify-center w-full px-1 py-2 mt-5 text-sm text-white transition-all duration-200 bg-blue-600 border border-transparent rounded-md hover:bg-blue-700'>
                            Explore Integrations <ArrowRight className='ml-1' />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

function CycleText({ title = 'Also works in' }) {
    const words = [
        'Word',
        'Excel',
        'Outlook',
        'PowerPoint',
        'Teams',
        'Telegram',
        'Chrome',
        'Gmail',
        'Google Slides',
        'Google Docs',
        'Google Sheets',
    ];
    const [index, setIndex] = useState(0);

    const total = words.length;
    useEffect(() => {
        const interval = setInterval(() => {
            setIndex((current) => (current + 1) % total);
        }, 1300);
        return () => clearInterval(interval);
    }, [total]);

    return (
        <span className='text-4xl font-bold text-black lg:text-5xl sm:text-5xl'>
            {title}&nbsp;
            <br />
            <AnimatePresence mode='wait'>
                <motion.h1
                    key={`words_${index}`}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -30 }}
                    transition={{ duration: 0.08 }}
                    className='inline-block text-blue-700'>
                    &quot;{words[index]}&quot;
                </motion.h1>
            </AnimatePresence>
        </span>
    );
}

export const integrationsList = [
    { name: 'Word', url: '/chatgpt-for-microsoft-word', imgSrc: WordLogo },
    { name: 'Excel', url: '/chatgpt-for-microsoft-excel', imgSrc: ExcelLogo },
    {
        name: 'PowerPoint',
        url: '/chatgpt-for-microsoft-powerpoint',
        imgSrc: PowerPointLogo,
    },
    { name: 'Teams', url: '/chatgpt-for-microsoft-teams', imgSrc: TeamsLogo },
    {
        name: 'Outlook',
        url: '/chatgpt-for-microsoft-outlook',
        imgSrc: OutlookLogo,
    },
    {
        name: 'OneNote',
        url: '/chatgpt-for-onenote',
        imgSrc: OneNoteLogo,
    },
    { name: 'Gmail', url: '/chatgpt-for-gmail', imgSrc: GmailLogo },
    /*
    { name: 'Google Docs', url: '/chatgpt-for-google-docs', imgSrc: DocsLogo },
    {
        name: 'Google Sheets',
        url: '/chatgpt-for-google-sheets',
        imgSrc: SheetsLogo,
    },
    {
        name: 'Google Slides',
        url: '/chatgpt-for-google-slides',
        imgSrc: SlidesLogo,
    },
    */
    { name: 'Telegram', url: '/chatgpt-for-telegram', imgSrc: TelegramLogo },
    { name: 'Chrome', url: '/chatgpt-for-chrome', imgSrc: ChromeLogo },
];

export function IntegrationsHeader({ title }) {
    return (
        <div className='px-4 mx-auto max-w-7xl sm:px-6 lg:px-8'>
            <div className='max-w-2xl mx-auto text-center'>
                <CycleText title={title} />
                <p className='mt-4 text-lg leading-relaxed text-gray-600'>
                    Stay in Word, Outlook, Excel, or PowerPoint — and work
                    smarter with AI in one click.
                </p>
            </div>
        </div>
    );
}

export function IntegrationsList({ title }) {
    return (
        <section className='py-12 bg-gray-50 sm:py-16 lg:py-20'>
            <IntegrationsHeader title={title} />

            <div className='mt-9 lg:mt-12 mx-4'>
                <EmblaCarouselForReviews
                    slides={integrationsList.map((item, idx) => (
                        <IntegrationCard
                            key={`integration-${idx}`}
                            item={item}
                        />
                    ))}
                />
            </div>
            <div className='mt-2 text-center md:mt-4'>
                <a
                    href='/integrations'
                    title='Browse All Integrations'
                    className='inline-flex items-center pb-2 text-base leading-7 text-gray-600 transition-all duration-200  hover:text-gray-900 font-pj focus:outline-none focus:ring-1 focus:ring-gray-900 focus:ring-offset-2 '>
                    Browse All Integrations <ArrowRight className='ml-1 h-4' />
                </a>
            </div>
        </section>
    );
}

export default () => (
    <MicroLandingNew
        hero={{
            imgUrl: WordGlory,
            title: 'ChatGPT plugin for Microsoft Word',
            subTitle: `Improve your writing, fix your tone, and say exactly what you mean — without leaving Microsoft Word.`,
            features: [
                '✅ Fix grammar, tone, and clarity — instantly',
                '🌍 Write better in English — or your own language',
                '🕒 Save hours every week',
                '🔁 Stay inside Word — always',
            ],
            samplePrompts: [
                'fix-grammar',
                'improve',
                'paraphrase',
                'summarize',
                'translate',
            ],
            feedback: {
                message: `This AI Assistant is a total game-changer for working on
                        writing projects. It's a real time-saver, helping you to
                        write better and fixing grammar mistakes, so the whole
                        process is much easier and faster.`,
                name: 'Fernando Rodriguez',
                role: 'E-commerce Director at Wayfair',
            },
            install: [
                {
                    label: 'Install for Word App',
                    url: '/chatgpt-for-microsoft-word/install/desktop',
                },
                {
                    label: 'Install for Word Online',
                    url: '/chatgpt-for-microsoft-word/install/online',
                },
            ],
        }}
        instruction={{
            title: 'How it works inside Word?',
            demoEl: <Demonstration videoDefinition={instruction} />,
            steps: [
                <>
                    <b>Open Word:</b> Highlight any part of the text in
                    Microsoft Word document
                </>,
                <>
                    <b>Use AI Perfect Assistant:</b> Open AI Perfect Assistant,
                    select any AI Prompt Template and Click "Generate with AI"
                </>,
                <>
                    <b>Insert result to the document:</b> Click "Insert" to
                    insert AI generated text to the Word document
                </>,
            ],
        }}
    />
);
